import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import api from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CookieBanner from "@/components/layout/CookieBanner";
import ChatWidget from "@/components/layout/ChatWidget";
import ThemeEngine from "@/components/layout/ThemeEngine";
import SeoHead from "@/components/layout/SeoHead";
import PromoBanner from "@/components/layout/PromoBanner";
import FreeCanvasRender from "@/components/layout/FreeCanvasRender";
import { initClickTracking, trackPageview } from "@/lib/tracker";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Univers from "@/pages/Univers";
import Services, { ServiceSuccess } from "@/pages/Services";
import Histoire from "@/pages/Histoire";
import Carte from "@/pages/Carte";
import LeBar from "@/pages/LeBar";
import Evenements, { TicketSuccess } from "@/pages/Evenements";
import Galerie from "@/pages/Galerie";
import Fidelite from "@/pages/Fidelite";
import Contact from "@/pages/Contact";
import Reservation, { ReservationSuccess, ReservationAnnulee } from "@/pages/Reservation";
import AEmporter, { OrderSuccess } from "@/pages/AEmporter";
import Comptoir, { ComptoirSuccess } from "@/pages/Comptoir";
import CartesCadeaux, { GiftCardSuccess } from "@/pages/CartesCadeaux";
import MonEspace from "@/pages/MonEspace";
import SoireeVip from "@/pages/SoireeVip";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import Admin from "@/pages/admin/Admin";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageview(pathname);
  }, [pathname]);
  return null;
}

function FloatingReserve() {
  const { pathname } = useLocation();
  let cta;
  if (pathname.startsWith("/reservation")) return null;
  if (pathname.startsWith("/services")) {
    cta = { to: "/services#devis", label: "Demander un devis", testId: "floating-quote-button" };
  } else if (pathname.startsWith("/comptoir")) {
    cta = { to: "/comptoir#boutique", label: "Découvrir la boutique", testId: "floating-shop-button" };
  } else {
    cta = { to: "/reservation", label: "Réserver une table", testId: "floating-reserve-button" };
  }
  return (
    <Link
      to={cta.to}
      data-testid={cta.testId}
      className="btn-primary fixed bottom-6 right-6 z-50 !h-12 !px-6 shadow-lg shadow-black/40"
    >
      {cta.label}
    </Link>
  );
}

function DesignProvider({ children }) {
  const [design, setDesign] = useState(null);
  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setDesign(r.data?.design || {}))
      .catch(() => setDesign({}));
  }, []);
  if (design === null) return children;

  const font = design.font || "Montserrat";
  const btnStyle = design.btn_style || "filled";
  const btnBorder = design.btn_border || "0";
  const textScale = design.text_scale || 1;
  const spacingScale = design.spacing_scale || 1;
  const copper = design.copper || "#C4704F";
  const ivory = design.ivory || "#F5F5F0";

  const css = `
    body{font-size:calc(16px * ${textScale})}
    .btn{border-width:${btnBorder}px!important}
    ${
      btnStyle === "outline"
        ? `.btn-primary{background-color:transparent!important;border-color:${copper}!important;color:${copper}!important}.btn-primary:hover{background-color:${copper}!important;color:${ivory}!important}`
        : ""
    }
    [data-testid="home-page"] section{padding-top:calc(7rem * ${spacingScale})!important;padding-bottom:calc(7rem * ${spacingScale})!important}
  `;

  return (
    <>
      {font !== "Montserrat" && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?family=${font.replace(/ /g, "+")}:wght@400;500;600;700&display=swap`}
        />
      )}
      <style>{css}</style>
      {children}
    </>
  );
}

const CANVAS_PAGES = { "/palais": "palais", "/comptoir": "comptoir", "/services": "services" };

function PublicShell({ children }) {
  const { pathname } = useLocation();
  const canvasPage = CANVAS_PAGES[pathname];
  return (
    <div className="min-h-screen bg-ink">
      <PromoBanner />
      <Header />
      <main>{children}</main>
      {canvasPage && <FreeCanvasRender page={canvasPage} />}
      <Footer />
      <FloatingReserve />
      <ChatWidget />
    </div>
  );
}

const page = (el) => <PublicShell>{el}</PublicShell>;

function App() {
  useEffect(() => {
    initClickTracking();
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <DesignProvider>
        <BrowserRouter>
        <ScrollToTop />
        <ThemeEngine />
        <SeoHead />
        <Routes>
          <Route path="/" element={<Univers />} />
          <Route path="/palais" element={page(<Home />)} />
          <Route path="/services" element={page(<Services />)} />
          <Route path="/services/succes" element={page(<ServiceSuccess />)} />
          <Route path="/notre-histoire" element={page(<Histoire />)} />
          <Route path="/carte" element={page(<Carte />)} />
          <Route path="/le-bar" element={page(<LeBar />)} />
          <Route path="/evenements" element={page(<Evenements />)} />
          <Route path="/evenements/succes" element={page(<TicketSuccess />)} />
          <Route path="/galerie" element={page(<Galerie />)} />
          <Route path="/fidelite" element={page(<Fidelite />)} />
          <Route path="/contact" element={page(<Contact />)} />
          <Route path="/reservation" element={page(<Reservation />)} />
          <Route path="/reservation/succes" element={page(<ReservationSuccess />)} />
          <Route path="/reservation/annulee" element={page(<ReservationAnnulee />)} />
          <Route path="/a-emporter" element={page(<AEmporter />)} />
          <Route path="/a-emporter/succes" element={page(<OrderSuccess />)} />
          <Route path="/comptoir" element={page(<Comptoir />)} />
          <Route path="/comptoir/succes" element={page(<ComptoirSuccess />)} />
          <Route path="/cartes-cadeaux" element={page(<CartesCadeaux />)} />
          <Route path="/cartes-cadeaux/succes" element={page(<GiftCardSuccess />)} />
          <Route path="/espace" element={page(<MonEspace />)} />
          <Route path="/soiree-vip" element={page(<SoireeVip />)} />
          <Route path="/mentions-legales" element={page(<Legal page="mentions-legales" />)} />
          <Route path="/politique-de-confidentialite" element={page(<Legal page="politique-de-confidentialite" />)} />
          <Route path="/cgv" element={page(<Legal page="cgv" />)} />
          <Route path="/politique-de-cookies" element={page(<Legal page="politique-de-cookies" />)} />
          <Route path="/gestion-des-cookies" element={page(<Legal page="gestion-des-cookies" />)} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={page(<Home />)} />
        </Routes>
        <CookieBanner />
        <Toaster theme="dark" position="top-center" />
        </BrowserRouter>
      </DesignProvider>
    </AuthProvider>
  );
}

export default App;
